import React, { useCallback, useMemo } from 'react';
import { formColumnsWidth } from '../../../pageForm/PageHeadersWidthConfig';
import GeneralFormItem from '../../../../components/GeneralFormItem';
import EditableTextArea from '../../../../components/EditableTextArea';
import {
  dataModelerSetUiDraftDerivedField,
  dataModelerUpdateExistingFieldInput,
} from '../../../../redux/slices/dataModeler';
import { entityDescriptionSchema } from '../../../../components/EditableTextArea/validation';
import TickCheckbox from '../../../../components/Checkbox/TickCheckbox';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  FieldClass,
  FieldType,
  IDataModelExtendFactFieldInput,
  IDataModelUpdateFieldInput,
  IRawSqlDefinitionInput,
  ISchemaFieldMetaFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import {
  CenteredDiv,
  NameWithIconWrapper,
  StyledFormUIRow,
} from '../DataModelerCommonUI';
import { fieldTypeName } from '../utils';
import KubitTooltip from '../../../../components/KubitTooltip';
import { dataModelTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import DataModelerFieldGridTableColumns from './DataModelerFieldGridTableColumns';
import { useViewColumnsValidator } from '../validation';
import DataModelerDerivedFieldDefinitionMeta from './DataModelerDerivedFieldDefinitionMeta';
import { fieldIsDirty } from '../../../../redux/slices/dataModeler/utils';
import { ModifiedIcon } from '../DataModelerShared';
import DerivedDefinitionIcon from '../../../../components/DerivedDefinitionIcon';
import { derivedFieldDefinitionInputToMeta } from './utils';
import { dataModelerSelectCurrentUiState } from '../../../../redux/selectors/dataModeler';

interface IProps {
  schemaKey: string;
  isEditMode: boolean;
  fieldMeta: ISchemaFieldMetaFieldsFragment;
  editInput?: IDataModelUpdateFieldInput;
  extendInputs: IDataModelExtendFactFieldInput[];
}

const DATA_MODEL_FIELD_CLASS_CAN_BE_DISABLED: Record<FieldClass, boolean> = {
  [FieldClass.Basic]: true,
  [FieldClass.Subject]: false, // TODO, disabling a subject may require extra Kobol work.
  [FieldClass.Event]: false,
  [FieldClass.Temporal]: false,
};

const DataModelerExistingFieldRow = ({
  schemaKey,
  fieldMeta,
  editInput,
  extendInputs,
  isEditMode,
}: IProps) => {
  const dispatch = useAppDispatch();
  const inputIsDirty = fieldIsDirty(fieldMeta, editInput);

  const editableValues = useMemo(() => {
    return {
      name: editInput?.name ?? fieldMeta.name,
      description: editInput?.description ?? fieldMeta.description,
      filter: editInput?.filter ?? fieldMeta.filter,
      groupby: editInput?.groupby ?? fieldMeta.groupby,
      deprecated: editInput?.deprecated ?? fieldMeta.deprecated,
      derivedDefinition: editInput?.derivedDefinition
        ? derivedFieldDefinitionInputToMeta(editInput.derivedDefinition)
        : fieldMeta.derivedDefinition,
    };
  }, [fieldMeta, editInput]);

  const hasActiveEditedDerivedField = !!useAppSelector(
    dataModelerSelectCurrentUiState,
  )?.draftDerivedField;
  const canEditDefinition =
    !hasActiveEditedDerivedField &&
    fieldMeta.derivedDefinition?.__typename === 'RawSqlDerivedFieldMeta';

  const handleEditDefinitionClick = useCallback(() => {
    if (fieldMeta.derivedDefinition?.__typename !== 'RawSqlDerivedFieldMeta') {
      return;
    }
    const rawSqlDefinition: IRawSqlDefinitionInput =
      editInput?.derivedDefinition?.rawSql ?? fieldMeta.derivedDefinition;
    dispatch(
      dataModelerSetUiDraftDerivedField({
        draft: {
          fieldName: editableValues.name,
          fieldType: fieldMeta.type,
          typeParams: null,
          rawSql: {
            rawSql: rawSqlDefinition.rawSql,
            fullDataRequired: rawSqlDefinition.fullDataRequired,
          },
          fieldMeta,
          newFieldKey: null,
        },
      }),
    );
  }, [
    dispatch,
    editInput?.derivedDefinition?.rawSql,
    editableValues.name,
    fieldMeta,
  ]);

  const groupByDisabled =
    !isEditMode ||
    (!!fieldMeta.derivedDefinition &&
      fieldMeta.type === FieldType.Array &&
      !editableValues.groupby);
  const groupByTooltip =
    isEditMode && groupByDisabled
      ? dataModelTooltipOptions.groupbyCheckboxTooltipDisabledContent
      : dataModelTooltipOptions.groupbyCheckboxTooltipContent;

  const { namesValidator } = useViewColumnsValidator({
    fieldKey: fieldMeta.key,
  });

  const deprecateActionDisabled =
    !DATA_MODEL_FIELD_CLASS_CAN_BE_DISABLED[fieldMeta.fieldClass];
  const deprecateActionContentPopupOpts = deprecateActionDisabled
    ? dataModelTooltipOptions.disableActionTooltipContent.prevented
    : editableValues.deprecated
      ? dataModelTooltipOptions.disableActionTooltipContent.disabled
      : dataModelTooltipOptions.disableActionTooltipContent.enabled;

  const handleNameChange = (newValue: string) => {
    dispatch(
      dataModelerUpdateExistingFieldInput({
        fieldMeta,
        input: {
          key: fieldMeta.key,
          name: newValue,
        },
      }),
    );
  };

  const handleDescriptionChange = (newValue: string) => {
    dispatch(
      dataModelerUpdateExistingFieldInput({
        fieldMeta,
        input: {
          key: fieldMeta.key,
          description: newValue,
        },
      }),
    );
  };

  const handleDeprecateClick = () => {
    if (deprecateActionDisabled) {
      return;
    }
    if (editableValues.deprecated) {
      // un-deprecating the field
      dispatch(
        dataModelerUpdateExistingFieldInput({
          fieldMeta,
          input: {
            key: fieldMeta.key,
            deprecated: false,
            filter: true,
          },
        }),
      );
    } else {
      dispatch(
        dataModelerUpdateExistingFieldInput({
          fieldMeta,
          input: {
            key: fieldMeta.key,
            deprecated: true,
            filter: false,
            groupby: false,
          },
        }),
      );
    }
  };

  const handleFilterClick = () => {
    // XXX also deprecate if disabling filter and groupby is already disabled (and deprecation is allowed)
    const deprecated = deprecateActionDisabled
      ? null
      : editableValues.filter && !editableValues.groupby;
    dispatch(
      dataModelerUpdateExistingFieldInput({
        fieldMeta,
        input: {
          key: fieldMeta.key,
          filter: !editableValues.filter,
          deprecated,
        },
      }),
    );
  };

  const handleGroupbyClick = () => {
    // XXX also deprecate if disabling filter and groupby is already disabled (and deprecation is allowed)
    const deprecated = deprecateActionDisabled
      ? null
      : editableValues.groupby && !editableValues.filter;
    dispatch(
      dataModelerUpdateExistingFieldInput({
        fieldMeta,
        input: {
          key: fieldMeta.key,
          groupby: !editableValues.groupby,
          deprecated,
        },
      }),
    );
  };

  return (
    <StyledFormUIRow
      $isHighlighted={inputIsDirty}
      $gridTemplateColumns={formColumnsWidth.dataModelerFieldsEditor}
      $isDisabled={editableValues.deprecated}
    >
      <GeneralFormItem
        key={'class'}
        label={fieldMeta.fieldClass}
        isDisabled={editableValues.deprecated}
        isBold={!editableValues.deprecated}
      />
      <NameWithIconWrapper>
        {fieldMeta.derivedDefinition && (
          <DerivedDefinitionIcon tooltipText={'Derived Field'} />
        )}
        <EditableTextArea
          isBold={!editableValues.deprecated}
          value={editableValues.name}
          disabled={!isEditMode || editableValues.deprecated}
          placeholder={''}
          onChange={handleNameChange}
          defaultRows={2}
          validation={namesValidator}
        />
      </NameWithIconWrapper>
      <EditableTextArea
        isBold={!editableValues.deprecated}
        value={editableValues.description ?? ''}
        disabled={!isEditMode || editableValues.deprecated}
        placeholder={''}
        onChange={handleDescriptionChange}
        defaultRows={4}
        validation={entityDescriptionSchema}
      />
      <GeneralFormItem
        key={'column'}
        label={fieldMeta.column}
        tooltipContent={fieldMeta.column}
        isDisabled={editableValues.deprecated}
        isBold={!editableValues.deprecated}
      />
      {editableValues.derivedDefinition ? (
        <DataModelerDerivedFieldDefinitionMeta
          definition={editableValues.derivedDefinition}
          isDisabled={!isEditMode || editableValues.deprecated}
          isBold={!editableValues.deprecated}
          canEdit={canEditDefinition}
          onEditClick={handleEditDefinitionClick}
          requireEditConfirmation={!editInput?.derivedDefinition}
        />
      ) : (
        <DataModelerFieldGridTableColumns
          isBold={!editableValues.deprecated}
          schemaKey={schemaKey}
          existing={fieldMeta.tableFields}
          added={null}
          extended={extendInputs}
        />
      )}
      <GeneralFormItem
        key={'type'}
        label={fieldTypeName(fieldMeta.type)}
        isDisabled={!isEditMode || editableValues.deprecated}
        isBold={!editableValues.deprecated}
      />
      <CenteredDiv>
        <KubitTooltip
          {...dataModelTooltipOptions.filterCheckboxTooltipContent}
          noOpenDelay={true}
        >
          <TickCheckbox
            disabled={!isEditMode}
            checked={editableValues.filter}
            onChange={handleFilterClick}
          />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        <KubitTooltip {...groupByTooltip} noOpenDelay={true}>
          <TickCheckbox
            disabled={groupByDisabled}
            checked={editableValues.groupby}
            onChange={handleGroupbyClick}
          />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        <KubitTooltip
          placement="top-start"
          noOpenDelay={true}
          {...deprecateActionContentPopupOpts}
        >
          <TickCheckbox
            checked={!deprecateActionDisabled && editableValues.deprecated}
            onChange={handleDeprecateClick}
            disabled={!isEditMode || deprecateActionDisabled}
          />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        {inputIsDirty && (
          <KubitTooltip
            {...dataModelTooltipOptions.modifiedFieldIcon}
            noOpenDelay={true}
          >
            <ModifiedIcon />
          </KubitTooltip>
        )}
      </CenteredDiv>
    </StyledFormUIRow>
  );
};

export default DataModelerExistingFieldRow;
